import { group, least, max, min, range } from 'd3-array';
import { axisBottom, axisLeft, AxisScale } from 'd3-axis';
import { scaleLinear, scaleTime } from 'd3-scale';
import { BaseType, pointer, select, Selection } from 'd3-selection';
import { line as d3Line } from 'd3-shape';

export type ScaleType = 'linear' | 'time';

export type ValueType = number | Date;

export type Domain = [number, number] | [Date, Date];

export type LineStyle = 'solid' | 'dashed';

export type TickFormatter = (value: any) => string;

export type LabelFormatter = (
    valueIndex: number,
    xValue: any,
    yValue: any,
    lineIndex: number,
    globalIndex: number
) => string;

interface DrawOptions {
    width: number;
    height: number;
    labelFormatter: LabelFormatter;
    labelXRotation: number;
    labelYRotation: number;
    lines: any[][];
    colors: string[];
    lineStyles: LineStyle[];
    marginX: number;
    marginY: number;
    paddingX: number;
    paddingY: number;
    propX: string;
    propY: string;
    scaleX: ScaleType;
    scaleY: ScaleType;
    ticksX?: number;
    ticksY?: number;
    tickFormatterX?: TickFormatter;
    tickFormatterY?: TickFormatter;
    debug: boolean;
    completeRange: boolean;
}

interface InternalData {
    // Tutti i valori per x.
    X: any[];
    // Mappaggio per i valori su x.
    xScale: AxisScale<ValueType>;
    // Tutti i valori per y.
    Y: any[];
    // Mappaggio per i valori su y.
    yScale: AxisScale<ValueType>;
    // Utilizziamo un finto asse z per decidere a che linea appartiene un punto.
    Z: number[];
    // Range dei valori da disegnare.
    I: number[];
}

export class LineGraphDrawer {
    private dot?: Selection<SVGGElement, unknown, null, undefined>;
    private path?: Selection<BaseType | SVGPathElement, any, SVGElement, any>;

    clear(root: SVGElement) {
        select(root).selectAll('*').remove();
    }

    draw(root: SVGElement, options: DrawOptions) {
        const [xScale, yScale] = this.calculateScales(options);

        const internalData = this.flattenData(options, xScale, yScale);

        this.doDraw(root, internalData, options);
    }

    private calculateScales(options: DrawOptions) {
        // La scala è un mappaggio da un dato ad una coordinata x o y, quindi bisogna tenere conto di margini e padding
        //   nei calcoli.
        const xMin = min(options.lines, (l) => min(l, (d) => d[options.propX]));
        const xMax = max(options.lines, (l) => max(l, (d) => d[options.propX]));
        let xDomain: Domain;
        if (options.scaleX === 'linear') {
            const xRange = xMax - xMin;
            const xPadding = (xRange / 100) * options.paddingX;
            xDomain = [xMin - xPadding, xMax + xPadding];
        } else {
            xDomain = [xMin, xMax];
        }

        const yMin = min(options.lines, (l) => min(l, (d) => d[options.propY]));
        const yMax = max(options.lines, (l) => max(l, (d) => d[options.propY]));
        let yDomain: Domain;
        if (options.scaleY === 'linear') {
            const yRange = yMax - yMin;
            const yPadding = (yRange / 100) * options.paddingY;
            yDomain = [yMin - yPadding, yMax + yPadding];
        } else {
            yDomain = [yMin, yMax];
        }

        return [
            this.getScale(options.scaleX)(xDomain, [0, options.width - 20 - 2 * options.marginX]),
            this.getScale(options.scaleY)(yDomain, [options.height - 2 * options.marginY, 0]),
        ];
    }

    private flattenData(
        options: DrawOptions,
        xScale: AxisScale<ValueType>,
        yScale: AxisScale<ValueType>
    ): InternalData {
        const xDomain = xScale.domain();
        const X = [];
        const Y = [];
        const Z = [];

        let numValues = 0;
        for (let i = 0; i < options.lines.length; ++i) {
            const line = options.lines[i]
                .slice()
                .sort((a, b) => a[options.propX] - b[options.propX]);
            numValues += line.length;

            // Se richiesto, crea un finto valore per avere una linea che incomincia sullo '0' delle ascisse.
            if (options.completeRange && line[0] && line[0][options.propX] > xDomain[0]) {
                X.push(xDomain[0]);
                Y.push(line[0][options.propY]);
                Z.push(i);
                ++numValues;
            }

            for (const datum of line) {
                X.push(datum[options.propX]);
                Y.push(datum[options.propY]);
                // La linea a cui appartiene questo punto.
                Z.push(i);
            }

            // Se richiesto, crea un finto valore per avere una linea che termina dove terminano le ascisse.
            if (
                options.completeRange &&
                line[line.length - 1] &&
                line[line.length - 1][options.propX] < xDomain[1]
            ) {
                X.push(xDomain[1]);
                Y.push(line[line.length - 1][options.propY]);
                Z.push(i);
                ++numValues;
            }
        }

        return {
            X,
            Y,
            Z,
            I: range(numValues),
            xScale,
            yScale,
        };
    }

    private getScale(
        scaleType: ScaleType
    ): (d: Domain, r: [number, number]) => AxisScale<ValueType> {
        switch (scaleType) {
            case 'time':
                return scaleTime;
            case 'linear':
            default:
                return scaleLinear;
        }
    }

    private doDraw(root: SVGElement, data: InternalData, options: DrawOptions) {
        const { X, Y, Z, I, xScale, yScale } = data;
        const xAxis = axisBottom(xScale)
            .tickFormat(options.tickFormatterX ?? this.makeTickFormatter(options.scaleX))
            .ticks(options.ticksX || data.X.length + 2);
        const yAxis = axisLeft(yScale)
            .tickFormat(options.tickFormatterY ?? this.makeTickFormatter(options.scaleY))
            .ticks(options.ticksY || data.Y.length);

        // "Disegnatore" di linee.
        const line = d3Line<any>()
            .x((i) => xScale(X[i]) ?? 0)
            .y((i) => yScale(Y[i]) ?? 0);

        // Pulisci il grafico.
        this.clear(root);

        const svg = select(root)
            .on('pointerenter', () => this.onPointerEnter(options))
            .on('pointermove', (e) => this.onPointerMove(e, data, options))
            .on('pointerleave', () => this.onPointerLeave(options))
            .on('touchstart', (event) => event.preventDefault())
            .append('g')
            .attr('transform', `translate(${options.marginX + 20}, ${options.marginY})`);

        // Asse delle ascisse.
        svg.append('g')
            .attr('transform', `translate(0, ${options.height - 2 * options.marginY})`)
            .call(xAxis)
            .selectAll('g')
            .select('text')
            .attr('transform', () =>
                options.labelXRotation ? `rotate(${options.labelXRotation})` : ''
            );

        // Asse delle ordinate.
        svg.append('g')
            .attr('transform', `translate(0, 0)`)
            .call(yAxis)
            .selectAll('g')
            .select('text')
            .attr('transform', () =>
                options.labelYRotation ? `rotate(${options.labelYRotation})` : ''
            );

        // Disegna il grafico.
        this.path = svg
            .append('g')
            .attr('fill', 'none')
            .attr('stroke', 'steelblue')
            .attr('stroke-width', 1.5)
            .selectAll('path')
            .data(group(I, (i) => Z[i]))
            .join('path')
            .style('mix-blend-mode', 'normal')
            .style('stroke', (_, i) => this.getColorByIndex(i, options))
            .style('stroke-dasharray', (_, i) =>
                this.getStyleByIndex(i, options) === 'dashed' ? 5 : 0
            );

        if (I.length === 1) {
            // Nel caso ci sia un solo dato, disegna una linea a mano partendo dallo 0.
            this.path.attr('d', `M0,${yScale(Y[0])}L${xScale(X[0])},${yScale(Y[0])}`);
        } else {
            // Altrimenti invoca il disegnatore di linee per ogni punto.
            this.path.attr('d', ([, i]) => line(i));
        }

        // Il punto che compare al passaggio del mouse.
        this.dot = svg.append('g').attr('display', null);
    }

    private makeTickFormatter(scale: ScaleType): any {
        if (scale === 'linear') {
            return null;
        }
        return (v: number) => new Date(v).toLocaleDateString();
    }

    private onPointerEnter(options: DrawOptions) {
        this.path?.style('stroke', (_, i) => this.getColorByIndex(i, options));
        this.dot?.attr('display', null);
    }

    private onPointerLeave(options: DrawOptions) {
        this.path?.style('stroke', (_, i) => this.getColorByIndex(i, options));
        this.dot?.attr('display', 'none');
    }

    private onPointerMove(
        event: unknown,
        { X, Y, Z, I, xScale, yScale }: InternalData,
        options: DrawOptions
    ) {
        const [pointerX, pointerY] = pointer(event);
        const [xm, ym] = [pointerX - options.marginX, pointerY - options.marginY];
        this.dot?.selectAll('*').remove();

        // L'indice del punto più vicino al mouse.
        const i = least(I, (i) => {
            const actualX = xScale(X[i]) ?? 0;
            const actualY = yScale(Y[i]) ?? 0;

            return Math.hypot(actualX - xm, actualY - ym);
        });
        if (i === undefined) {
            return;
        }

        // Colora la linea.
        this.path
            ?.style('stroke', ([z]) => (Z[i] === z ? null : '#ddd'))
            .filter(([z]) => Z[i] === z)
            .raise();

        // Mostra il punto con label.
        this.dot?.attr('transform', `translate(${xScale(X[i])}, ${yScale(Y[i])})`);
        this.dot?.append('circle').attr('r', 2.5).attr('fill', 'steelblue');
        const rect = this.dot
            ?.append('rect')
            .attr('stroke', 'steelblue')
            .attr('class', 'graph-box');
        const text = this.dot
            ?.append('text')
            .attr('text-anchor', 'middle')
            .attr('fill', 'currentColor')
            .attr('font-family', 'sans-serif')
            .attr('font-size', 10)
            .attr('y', -8);

        //Se la riga selezionata è uno degli esami mostro data esame altrimenti il valore max/min
        text?.text(options.labelFormatter(i, X[i], Y[i], Z[i], I[i]));

        const bbox = text?.node()?.getBBox();
        if (bbox) {
            const padding = 2;
            rect?.attr('x', bbox.x - padding)
                .attr('y', bbox.y - padding)
                .attr('width', bbox.width + padding * 2)
                .attr('height', bbox.height + padding * 2);
        }
    }

    private getColorByIndex(index: number, options: DrawOptions): string {
        return options.colors[index % options.colors.length];
    }

    private getStyleByIndex(index: number, options: DrawOptions): LineStyle {
        return options.lineStyles[index] ?? 'solid';
    }
}
